import React from 'react';
import {
  Action,
  ActionDropDown,
  ActionIconDropdown, HStack, ICON_CHECK, ICON_CUSTOM_SORT_PLUS, ICON_SORT,
  Message,
  Select, Stack,
  CardSkeleton,
  Card,
  ICON_ACCOUNT_MULTIPLE_OUTLINE,
  SimpleTable,
  SimpleTableSkeleton,
  Spacing,
} from '@doveit/bricks';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useFilters from '../../../hooks/use-filters/useFilters';
import {
  DEFAULT_SORT_OPTION, FILTER_OPTIONS, FILTERS_MAPPER, GROUPED_FILTER_OPTIONS, LOAD_SERP_AGENTS_ERROR_MESSAGE, MOST_RECENT_STARTED_SORT_OPTION, NO_SERP_AGENTS, SORT_OPTIONS, ALL_AGENTS_FILTER_OPTION,
  SORT_OPTIONS_PER_FILTER,
} from './constants';
import { useSerpAgents } from '../../hooks/use-serp-agents/useSerpAgents';
import usePagination from '../../../hooks/use-pagination/usePagination';
import { AgentSerpPageFilterKeys } from './types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import SerpFiltersSummary from '../../../components/serp-filters-summary/SerpFiltersSummary';
import PaginatedList from '../../../components/paginated-list/PaginatedList';
import ViewSerpAgentTableRow from '../../containers/view-serp-agent-table-row/ViewSerpAgentTableRow';
import AgentFilterAction from '../../../components/agent-filter-action/AgentFilterAction';
import { useAgentFilter } from '../../../hooks/agent-filter/useAgentFilter';
import getShortAgentName from '../../../utils/agent/getShortAgentName';
import { JobTitle } from '../../../domain/types/jobTitle';
import ViewSerpAgentCard from '../../containers/view-serp-agent-card/ViewSerpAgentCard';

const AgentSerpPage: React.FC = () => {
  const {
    userIsAdmin, userIsCallCenter, userIsContentEditor, userIsHR,
  } = useRBAC();
  const isMobile = useIsDevice('mobile');
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { page, paginationQueryParam, goToPage } = usePagination('page', scrollRef);
  const canViewAgentPage = React.useMemo(() => userIsAdmin || userIsContentEditor || userIsHR, [userIsAdmin, userIsContentEditor, userIsHR]);

  const canViewAgentGoalAndRank = React.useMemo(() => !userIsContentEditor && !userIsCallCenter, [userIsCallCenter, userIsContentEditor]);

  const {
    agentEmails: agents,
    replaceFilter: replaceAgentFilter,
    isLoading: isAgentFilterLoading,
    isExplicit: isAgentFilterExplicit,
  } = useAgentFilter(paginationQueryParam, getShortAgentName);
  const areaManagerIds = React.useMemo(() => agents.filter((it) => !!it.id).map((it) => it.id!!), [agents]);

  const {
    filters,
    addFilters,
    removeFilters,
  } = useFilters(FILTERS_MAPPER);

  const sortOption = React.useMemo(() => {
    if (filters.sort && filters.sort !== DEFAULT_SORT_OPTION.key) {
      return SORT_OPTIONS.find(({ key }) => key === filters.sort);
    }

    if (filters.filter) {
      return SORT_OPTIONS_PER_FILTER[filters.filter!];
    }

    return MOST_RECENT_STARTED_SORT_OPTION;
  }, [filters.filter, filters.sort]);

  const { data: serpAgents, isLoading: areSerpAgentsLoading, error: serpAgentsError } = useSerpAgents(isAgentFilterLoading
    ? undefined
    : {
      q: filters?.q,
      ...(FILTER_OPTIONS.find(({
        key,
      }) => key === filters.filter)?.value),
      areaManagerId: areaManagerIds,
      ...(FILTER_OPTIONS.find(({
        key,
      }) => key === filters.filter)?.value),
    }, {
    page,
    sort: sortOption?.value,
  });

  const filterLabel = React.useMemo(() => {
    if (filters.filter === ALL_AGENTS_FILTER_OPTION.key) {
      return '';
    }

    const filterObject = FILTER_OPTIONS.find(({ key }) => key === filters.filter);

    return filterObject?.label.long;
  }, [filters.filter]);

  const handleBadgeClick = React.useCallback((badgeFilter: AgentSerpPageFilterKeys) => () => {
    addFilters({ filter: badgeFilter }, { deleteKeys: [paginationQueryParam] });
  }, [addFilters, paginationQueryParam]);

  const handleSearch = React.useCallback((query: string) => {
    if (query) {
      addFilters({ q: query }, { deleteKeys: [paginationQueryParam] });
    } else {
      removeFilters(['q', paginationQueryParam]);
    }
  }, [addFilters, paginationQueryParam, removeFilters]);

  return (
    <SimplePageLayout
      toolbar={(
        <HStack
          alignItems="center"
          justifyContent="space-between"
          gap={100}
        >
          <div style={{ flexGrow: '1' }}>
            {!isMobile && (
              <HStack aria-label="Filtri (desktop)" gap={150}>
                {GROUPED_FILTER_OPTIONS.map(({ label, options }) => (
                  <ActionDropDown
                    key={label}
                    label={label}
                    aria-label={`Filtra agenti per ${label}`}
                    color={options.some(({ key }) => key === filters.filter) ? 'primary' : 'neutral'}
                    emphasis="low"
                    options={options.map(({ key, label: optionLabel }) => ({
                      key,
                      label: optionLabel.short,
                      color: filters.filter !== key ? 'neutral' : 'primary',
                      iconRight: filters.filter !== key ? undefined : { path: ICON_CHECK },
                      onClick: filters.filter !== key ? handleBadgeClick(key) : undefined,
                    }))}
                  />
                ))}
                <Action
                  label={ALL_AGENTS_FILTER_OPTION.label.short}
                  aria-label="Tutti gli agenti"
                  onClick={filters.filter !== ALL_AGENTS_FILTER_OPTION.key
                    ? handleBadgeClick(ALL_AGENTS_FILTER_OPTION.key)
                    : undefined}
                  color={filters.filter !== ALL_AGENTS_FILTER_OPTION.key
                    ? 'neutral'
                    : 'primary'}
                />
              </HStack>
            )}
            {isMobile && (
              <Select
                value={filters.filter}
                aria-label="Seleziona un filtro degli agenti (mobile)"
                options={FILTER_OPTIONS.map(({ label, key }) => ({
                  label: label.long,
                  value: key,
                }))}
                size="S"
                onChange={({ target }) => handleBadgeClick(target.value as AgentSerpPageFilterKeys)()}
              />
            )}
          </div>

          <HStack
            alignItems="center"
            gap={100}
          >
            <SearchByQueryAction
              initialQuery={filters.q}
              onSearch={handleSearch}
              width="20rem"
              placeholder="Cerca per nome, tel., email..."
              size={!isMobile ? 'M' : 'S'}
              variant={!isMobile ? 'input' : 'action'}
            />
            <AgentFilterAction
              size={!isMobile ? 'M' : 'S'}
              jobTitles={[JobTitle.AREA_MANAGER, JobTitle.BROKER, JobTitle.EXECUTIVE]}
              onSubmit={(emails) => replaceAgentFilter(emails)()}
            />
            <ActionIconDropdown
              label="Ordina"
              aria-label="Pulsante per selezionare l'ordinamento degli agenti"
              placement="bottom-end"
              icon={{ path: !filters.sort || filters.sort !== DEFAULT_SORT_OPTION.key ? ICON_CUSTOM_SORT_PLUS : ICON_SORT }}
              size={!isMobile ? 'M' : 'S'}
              color={filters.sort && filters.sort !== DEFAULT_SORT_OPTION.key ? 'primary' : 'neutral'}
              emphasis="low"
              options={SORT_OPTIONS.map(({ key, label }) => ({
                key,
                label,
                iconRight: (!filters.sort && key === DEFAULT_SORT_OPTION.key) || filters.sort === key ? { path: ICON_CHECK } : undefined,
                color: (key !== DEFAULT_SORT_OPTION.key && filters.sort === key)
                  ? 'primary'
                  : 'neutral',
                onClick: () => addFilters({ sort: key }),
              }))}
            />
          </HStack>
        </HStack>
      )}
    >
      <Card
        paddingSize="M"
        aria-label="Lista agenti"
      >
        <Card.Header
          icon={{ path: ICON_ACCOUNT_MULTIPLE_OUTLINE }}
          title={`${filterLabel ? ` ${filterLabel}` : 'Agenti'}${(serpAgents && serpAgents.totalElements > 0) ? ` (${serpAgents.totalElements})` : ''}`}
        />
        <Card.Content>
          <SerpFiltersSummary
            aria-label="search-filters-summary"
            filters={{ ...filters, agentFilters: { values: agents, isExplicit: isAgentFilterExplicit } }}
            sortOptions={SORT_OPTIONS}
            isAreaManagerAgentFilter
          />
          <Spacing
            margin={[300, 0, 0]}
          >
            {areSerpAgentsLoading && !isMobile && (
              <SimpleTableSkeleton aria-label="Agenti in caricamento (desktop)" />
            )}

            {areSerpAgentsLoading && isMobile && (
              <Stack aria-label="Agenti in caricamento (mobile)">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </Stack>
            )}

            {serpAgentsError && !areSerpAgentsLoading && (
              <Message
                message={LOAD_SERP_AGENTS_ERROR_MESSAGE}
                type="critical"
                boxed
              />
            )}

            {(!areSerpAgentsLoading && (serpAgents && serpAgents.totalElements > 0)) && (
              <PaginatedList
                {...serpAgents}
                hideResultCount
                goToPage={goToPage}
              >
                {isMobile && serpAgents.content.map((serpAgent) => (
                  <ViewSerpAgentCard
                    serpAgent={serpAgent}
                    canViewAgentPage={canViewAgentPage}
                    canViewAgentGoal={canViewAgentGoalAndRank}
                    canViewAgentRank={canViewAgentGoalAndRank}
                  />
                ))}
                {!isMobile && (
                  <SimpleTable>
                    <SimpleTable.Body>
                      {serpAgents.content.map((serpAgent) => (
                        <SimpleTable.Row
                          asChild
                          key={serpAgent.agent.id}
                        >
                          <ViewSerpAgentTableRow
                            serpAgent={serpAgent}
                            canViewAgentPage={canViewAgentPage}
                            canViewAgentGoal={canViewAgentGoalAndRank}
                            canViewAgentRank={canViewAgentGoalAndRank}
                          />
                        </SimpleTable.Row>
                      ))}
                    </SimpleTable.Body>
                  </SimpleTable>
                )}
              </PaginatedList>
            )}
            {(serpAgents && serpAgents.totalElements === 0) && (
              <Message message={NO_SERP_AGENTS} boxed />
            )}
          </Spacing>
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default AgentSerpPage;
